import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createReducer, createSelector, on } from '@ngrx/store';
import moment from 'moment';
import {
    AddNoteToCaseFailure,
    GenerateInhCertificate,
    GenerateInhCertificateFailure,
    GenerateInhCertificateSuccess,
    LoadEmployeeCaseById,
    LoadEmployeeCasesFailure,
    LoadEmployeeCasesSuccess,
    RemoveEmployeeCaseAttachmentFailure,
    RemoveNoteFromCaseFailure,
    UpdateCase,
    UpdateCaseFailure,
    UpdateCaseStatus,
    UpdateCaseStatusFailure,
    UpsertCaseAttributeFailure,
    UpsertEmployeeCaseAttachmentFailure
} from '../actions/employee-case.actions';
import { buildEmployeeCaseEntities } from '../entity-builder-utils';
import { EmployeeCaseEntity } from '../entity-models';
import { AppState } from '../store';

export interface EmployeeCaseState extends EntityState<EmployeeCaseEntity> {
    generatingInhCertificate: boolean;
    error: any;
    caseByIdLoading: boolean;
}

function selectEmployeeCaseId(a: EmployeeCaseEntity): string {
    return a.employeeCaseId;
}
function sortEmployeeCases(a: EmployeeCaseEntity, b: EmployeeCaseEntity): number {
    return moment(b.creationDateTime).toDate().getTime() - moment(a.creationDateTime).toDate().getTime();
}

export const employeeCaseAdapter: EntityAdapter<EmployeeCaseEntity> = createEntityAdapter<EmployeeCaseEntity>({
    selectId: selectEmployeeCaseId,
    sortComparer: sortEmployeeCases
});

export const initialState: EmployeeCaseState = employeeCaseAdapter.getInitialState({
    generatingInhCertificate: false,
    error: undefined,
    caseByIdLoading: false
});

export const employeeCaseReducer = createReducer(
    initialState,
    on(LoadEmployeeCaseById, (state) => ({
        ...state,
        caseByIdLoading: true
    })),

    on(LoadEmployeeCasesSuccess, (state, { cases }) =>
        employeeCaseAdapter.upsertMany(buildEmployeeCaseEntities(...(cases || [])), {
            ...state,
            caseByIdLoading: false
        })
    ),

    on(LoadEmployeeCasesFailure, (state, { error }) => ({ ...state, error, caseByIdLoading: false })),

    on(
        AddNoteToCaseFailure,
        RemoveNoteFromCaseFailure,
        UpdateCaseFailure,
        UpdateCaseStatusFailure,
        UpsertCaseAttributeFailure,
        UpsertEmployeeCaseAttachmentFailure,
        RemoveEmployeeCaseAttachmentFailure,
        (state, { error }) => ({ ...state, error })
    ),

    on(UpdateCase, UpdateCaseStatus, (state) => ({ ...state, error: undefined })),

    on(GenerateInhCertificate, (state) => ({ ...state, generatingInhCertificate: true })),
    on(GenerateInhCertificateSuccess, GenerateInhCertificateFailure, (state) => ({ ...state, generatingInhCertificate: false }))
);

const { selectAll } = employeeCaseAdapter.getSelectors();

export const employeeCaseState = (state: AppState) => state.employeeCaseState;
export const employeeCases = createSelector(employeeCaseState, selectAll);

export const employeeCaseByIdLoading = createSelector(employeeCaseState, ({ caseByIdLoading }) => caseByIdLoading);
export const generatingInhCertificate = createSelector(employeeCaseState, (state) => state.generatingInhCertificate);

export const employeeCasesError = createSelector(employeeCaseState, ({ error }) => error);

export const employeeCaseByEmployeeCaseId = (caseId: string) =>
    createSelector(employeeCases, (employeeCases) => employeeCases.find((employeeCase) => employeeCase.employeeCaseId === caseId));

export const employeeCaseByEmployeeNumber = (employeeCaseNumber: string) =>
    createSelector(employeeCases, (employeeCases) =>
        employeeCases.find((employeeCase) => employeeCase.employeeCaseNumber === employeeCaseNumber)
    );
